import React, { useEffect, useState } from "react";
import api from "../../services/api";
import {
  PageContainer, PageHeader, Card,
  Table, Thead, Th, TRow, Td,
  Muted,
} from "../ui";

interface MatrixCell {
  id: string;
  category: string;
  package_type: string;
  margin_percent: number;
  notes: string | null;
}

interface DualPricingSettings {
  card_markup_percent: number;
  sales_tax_rate: number;
  default_rounding_strategy: string;
}

const CATEGORIES = ["domestic", "import", "craft", "seltzer", "rtd", "malternative", "cider", "na"];
const PACKAGE_TYPES = ["case_full", "12pk", "8pk", "6pk", "4pk", "single", "slushie", "growler", "crowler", "pint", "taster"];

export default function PricingMatrix() {
  const [cells, setCells] = useState<MatrixCell[]>([]);
  const [settings, setSettings] = useState<DualPricingSettings | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [editMode, setEditMode] = useState(false);
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    const [cellsRes, settingsRes] = await Promise.all([
      api.get("/pricing/matrix"),
      api.get("/pricing/settings"),
    ]);
    setCells(cellsRes.data);
    setSettings(settingsRes.data);
  };

  const getCell = (category: string, packageType: string): MatrixCell | undefined => {
    return cells.find((c) => c.category === category && c.package_type === packageType);
  };

  const saveCell = async (category: string, packageType: string) => {
    const value = Number(editValue);
    if (isNaN(value) || value <= 0) {
      setEditing(null);
      return;
    }
    await api.put("/pricing/matrix", {
      category,
      package_type: packageType,
      margin_percent: value,
    });
    setEditing(null);
    loadData();
  };

  const updateSettings = async (field: string, value: string) => {
    await api.put("/pricing/settings", { [field]: Number(value) });
    loadData();
  };

  const cellKey = (cat: string, pkg: string) => `${cat}:${pkg}`;

  const enterEditMode = () => {
    const d: Record<string, string> = {};
    cells.forEach((c) => {
      d[cellKey(c.category, c.package_type)] = String(Number(c.margin_percent));
    });
    setDrafts(d);
    setEditMode(true);
  };

  const saveDraft = async (category: string, packageType: string) => {
    const key = cellKey(category, packageType);
    const raw = (drafts[key] || "").trim();
    const value = Number(raw);
    const existing = getCell(category, packageType);
    if (!raw || isNaN(value) || value <= 0) {
      // revert to whatever it was — blank/invalid never writes
      setDrafts((prev) => ({ ...prev, [key]: existing ? String(Number(existing.margin_percent)) : "" }));
      return;
    }
    if (existing && Number(existing.margin_percent) === value) return;
    await api.put("/pricing/matrix", {
      category,
      package_type: packageType,
      margin_percent: value,
    });
    loadData();
  };

  return (
    <PageContainer>
      <PageHeader
        title="Pricing Matrix"
        subtitle="Set margin percentages by category and package type"
      />

      {settings && (
        <Card className="mb-6">
          <div className="flex gap-6">
            <div>
              <label className="block text-xs uppercase mb-1 font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Card Markup %</label>
              <input
                type="number"
                step="0.01"
                defaultValue={settings.card_markup_percent}
                onBlur={(e) => updateSettings("card_markup_percent", e.target.value)}
                className="w-24 px-2 py-1 rounded-lg text-center outline-none"
                style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
              />
            </div>
            <div>
              <label className="block text-xs uppercase mb-1 font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Sales Tax Rate</label>
              <input
                type="number"
                step="0.0001"
                defaultValue={settings.sales_tax_rate}
                onBlur={(e) => updateSettings("sales_tax_rate", e.target.value)}
                className="w-24 px-2 py-1 rounded-lg text-center outline-none"
                style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
                onFocus={(e) => (e.target.style.borderColor = "var(--rbw-blue)")}
              />
            </div>
            <div>
              <label className="block text-xs uppercase mb-1 font-semibold" style={{ color: "var(--rbw-gray-dark)" }}>Rounding</label>
              <select
                defaultValue={settings.default_rounding_strategy}
                onChange={(e) => updateSettings("default_rounding_strategy", e.target.value)}
                className="px-2 py-1 rounded-lg outline-none"
                style={{ border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)" }}
              >
                <option value="none">None</option>
                <option value="nearest_5_cents">Nearest 5 cents</option>
                <option value="nearest_25_cents">Nearest 25 cents</option>
                <option value="nearest_99_cents">Nearest 99 cents</option>
              </select>
            </div>
          </div>
        </Card>
      )}

      <div className="flex justify-end mb-3">
        <button
          onClick={() => (editMode ? setEditMode(false) : enterEditMode())}
          className="px-4 py-2 rounded-xl text-sm font-bold transition-all active:scale-95"
          style={
            editMode
              ? { background: "var(--rbw-success)", color: "#fff" }
              : { background: "var(--rbw-gold)", color: "var(--rbw-navy)" }
          }
        >
          {editMode ? "Done Editing" : "Edit Matrix"}
        </button>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <Thead>
            <Th>Category</Th>
            {PACKAGE_TYPES.map((pt) => (
              <Th key={pt} align="center">{pt.replace("_", " ")}</Th>
            ))}
          </Thead>
          <tbody>
            {CATEGORIES.map((cat, idx) => (
              <TRow key={cat} index={idx}>
                <Td bold>{cat}</Td>
                {PACKAGE_TYPES.map((pt) => {
                  const cell = getCell(cat, pt);
                  const key = cellKey(cat, pt);
                  const isEditing = editing === key;

                  if (editMode) {
                    return (
                      <Td key={pt} align="center">
                        <input
                          type="number"
                          step="0.1"
                          placeholder="--"
                          value={drafts[key] ?? ""}
                          onChange={(e) => setDrafts((prev) => ({ ...prev, [key]: e.target.value }))}
                          onBlur={() => saveDraft(cat, pt)}
                          onKeyDown={(e) => e.key === "Enter" && (e.target as HTMLInputElement).blur()}
                          className="w-16 px-1 py-0.5 rounded-lg text-center text-sm outline-none"
                          style={{ border: "2px solid var(--rbw-blue)", color: "var(--rbw-navy)", background: "var(--rbw-bg-card)" }}
                        />
                      </Td>
                    );
                  }

                  return (
                    <Td key={pt} align="center">
                      {isEditing ? (
                        <input
                          type="number"
                          step="0.1"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          onBlur={() => saveCell(cat, pt)}
                          onKeyDown={(e) => e.key === "Enter" && saveCell(cat, pt)}
                          className="w-16 px-1 py-0.5 rounded-lg text-center text-sm outline-none"
                          style={{ border: "2px solid var(--rbw-blue)", color: "var(--rbw-navy)" }}
                          autoFocus
                        />
                      ) : (
                        <button
                          onClick={() => {
                            setEditing(key);
                            setEditValue(cell ? String(cell.margin_percent) : "");
                          }}
                          className="w-16 px-1 py-0.5 rounded-lg text-sm cursor-pointer font-medium transition-all"
                          style={cell
                            ? { background: "#8BB8E820", color: "var(--rbw-blue-dark)", border: "1px solid #8BB8E840" }
                            : { background: "transparent", color: "var(--rbw-gray)", border: "1px solid transparent" }
                          }
                        >
                          {cell ? `${Number(cell.margin_percent)}%` : "--"}
                        </button>
                      )}
                    </Td>
                  );
                })}
              </TRow>
            ))}
          </tbody>
        </Table>
      </div>

      <Muted>
        <p className="text-sm mt-3">
          Tap Edit Matrix to change margin % (or click any cell directly). Changes save when you leave a cell.
          Empty cells (--) mean that category/format combination must be priced manually.
        </p>
      </Muted>
    </PageContainer>
  );
}
